import StudioBrand from './StudioBrand';
import { useEffect, useMemo, useState } from 'react';
import {
  Accessibility, Baby, Banknote, Building2, Bus, Car, Clock3, Coffee, HeartHandshake,
  Map, Pill, RotateCcw, Save, Send, ShieldCheck, Undo2, VolumeX, Wifi,
} from 'lucide-react';
import { trusts } from '../lib/trustLibrary';
import { visibleTrusts } from '../lib/accessApi';
import { HOSPITAL_INFO_SECTIONS, normaliseHospitalInfo } from '../lib/hospitalInfo';
import { getEnabledLanguageIds, LANGUAGES, normalizeEnabledLanguageIds } from '../../../src/i18n/languages.js';
import { loadTrustConfig, saveTrustConfig } from '../lib/trustConfigStore';
import { loadCloudTrustConfig, runTrustWorkflowAction, saveCloudTrustConfig } from '../lib/trustConfigApi';
import { trustTranslationCoverage } from '../../../src/lib/trustTranslationSourceAudit.js';
import { englishLanguageName } from '../lib/studioLanguageLabels.js';

const ICONS = { Accessibility, Baby, Banknote, Building2, Bus, Car, Clock3, Coffee, HeartHandshake, Map, Pill, VolumeX, Wifi };

export default function HospitalInfoStudio({ workspaceSwitcher = null, studioAccess = null, studioTrusts = trusts }) {
  const scopedTrusts = visibleTrusts(studioAccess, studioTrusts);
  const initialTrustId = scopedTrusts.find((trust) => trust.id !== 'generic_nhs')?.id || scopedTrusts[0]?.id || 'generic_nhs';
  const [trustId, setTrustId] = useState(initialTrustId);
  const [config, setConfig] = useState(() => loadTrustConfig(initialTrustId));
  const [draft, setDraft] = useState(() => normaliseHospitalInfo(loadTrustConfig(initialTrustId).hospitalInfo));
  const [languageDraft, setLanguageDraft] = useState(() => loadTrustConfig(initialTrustId).enabledLanguages);
  const [sync, setSync] = useState('connecting');
  const [notice, setNotice] = useState('');
  const [workflow, setWorkflow] = useState({});
  const [reviewComment, setReviewComment] = useState('');
  const [publication, setPublication] = useState({ live: false, historyCount: 0 });
  const [audit, setAudit] = useState([]);
  const platformAdmin = studioAccess?.roles?.includes('platform_admin');
  const locked = config.status !== 'draft';
  const trustTextAudit = useMemo(() => trustTranslationCoverage(config, trustId), [config, trustId]);
  const downloadTrustTextAudit = () => {
    // Local-only handoff. Never POST Trust-authored source text to analytics.
    // Export must be clearly marked as an audit, not a translation approval.
    const columns = ['trustId', 'configStatus', 'configUpdatedAt', 'id', 'sourceText', 'sourceRevision', 'reviewScope', 'feature', 'path'];
    const csvCell = (value) => {
      const safe = String(value ?? '').replace(/^\s*([=+@-])/, "'$1");
      return '"' + safe.replace(/"/g, '""') + '"';
    };
    const csv = [columns.join(','), ...trustTextAudit.sources.map((row) =>
      columns.map((column) => csvCell({
        ...row, configStatus: config.status, configUpdatedAt: config.updatedAt || '',
      }[column])).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kyc-trust-${trustId}-local-copy-audit.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setNotice('Trust copy audit downloaded for controlled local language review. This is not an approval or patient publication.');
  };

  const load = async (nextTrustId) => {
    setTrustId(nextTrustId);
    setSync('connecting');
    try {
      const response = await loadCloudTrustConfig(nextTrustId);
      const next = response.configuration;
      saveTrustConfig(nextTrustId, next);
      setConfig(next);
      setDraft(normaliseHospitalInfo(next.hospitalInfo));
      setLanguageDraft(normalizeEnabledLanguageIds(next.enabledLanguages));
      setWorkflow(response.workflow || {});
      setPublication(response.publication || { live: false, historyCount: 0 });
      setAudit(response.audit || []);
      setSync('connected');
    } catch {
      const local = loadTrustConfig(nextTrustId);
      setConfig(local);
      setDraft(normaliseHospitalInfo(local.hospitalInfo));
      setLanguageDraft(normalizeEnabledLanguageIds(local.enabledLanguages));
      setSync('local');
      setNotice('Cloud configuration is unavailable. This browser copy is shown as a fallback.');
    }
  };

  useEffect(() => { load(initialTrustId); }, []);

  const runAction = async (action) => {
    try {
      const response = await runTrustWorkflowAction(trustId, action, reviewComment);
      saveTrustConfig(trustId, response.configuration);
      setConfig(response.configuration);
      setWorkflow(response.workflow || {});
      setPublication(response.publication || { live: false, historyCount: 0 });
      setAudit(response.audit || []);
      setReviewComment('');
      if (action === 'submit') setNotice('Submitted for review. The Trust draft is now locked.');
      else if (action === 'withdraw') setNotice('Submission withdrawn. The Trust draft is open for editing again.');
      else setNotice(`Trust profile moved to ${response.configuration.status}.`);
    } catch (error) {
      setNotice(error.message || 'The workflow action could not be completed.');
    }
  };

  const update = (sectionId, patch) => {
    setDraft((current) => ({
      ...current,
      [sectionId]: { ...current[sectionId], ...patch },
    }));
  };

  const invalid = useMemo(() => HOSPITAL_INFO_SECTIONS.some(({ id }) => {
    const item = draft[id];
    if (item.state !== 'available') return false;
    try {
      const parsed = new URL(item.url);
      return parsed.protocol !== 'https:' || Boolean(parsed.username || parsed.password) || !parsed.hostname.includes('.');
    } catch { return true; }
  }), [draft]);

  const save = async () => {
    if (invalid) {
      setNotice('Every available card needs a secure https URL.');
      return;
    }
    const next = { ...config, hospitalInfo: normaliseHospitalInfo(draft), enabledLanguages: normalizeEnabledLanguageIds(languageDraft) };
    const local = saveTrustConfig(trustId, next);
    setConfig(local);
    try {
      const response = await saveCloudTrustConfig(trustId, local);
      saveTrustConfig(trustId, response.configuration);
      setConfig(response.configuration);
      setDraft(normaliseHospitalInfo(response.configuration.hospitalInfo));
      setLanguageDraft(normalizeEnabledLanguageIds(response.configuration.enabledLanguages));
      setSync('connected');
      setNotice('Know Your Hospital configuration saved securely to the cloud.');
    } catch {
      setSync('local');
      setNotice('Saved in this browser only. Cloud sync must succeed before pilot publication.');
    }
  };

  return <div className="app-shell">
    <header className="topbar">
      <div className="brand"><span className="brand-mark"><Building2/></span><div><strong><StudioBrand /></strong><small>Know Your Hospital configuration</small></div></div>
      <div className="topbar-actions">
        {scopedTrusts.length > 1 && <label className="context-select"><select value={trustId} onChange={(event) => load(event.target.value)}>{scopedTrusts.map((trust) => <option key={trust.id} value={trust.id}>{trust.shortName}</option>)}</select></label>}
        <span className={`cloud-status ${sync}`}><i/>{sync === 'connected' ? 'Cloud connected' : sync === 'connecting' ? 'Connecting' : 'Local fallback'}</span>
<span className="trust-workflow-status"><ShieldCheck size={16}/>{config.status || 'draft'}</span>
        {config.status === 'draft' && <button className="button primary" onClick={save} disabled={invalid}><Save size={17}/>Save hospital information</button>}
        {!platformAdmin && config.status === 'draft' && <button className="button secondary" onClick={() => runAction('submit')} disabled={invalid}><Send size={17}/>Submit for review</button>}
        {!platformAdmin && config.status === 'submitted' && <button className="button secondary" onClick={() => runAction('withdraw')}><Undo2 size={17}/>Withdraw submission</button>}
        {platformAdmin && config.status === 'submitted' && <><button className="button secondary" onClick={() => runAction('request_changes')}><Undo2 size={17}/>Request changes</button><button className="button primary" onClick={() => runAction('approve')}><ShieldCheck size={17}/>Approve</button></>}
        {platformAdmin && config.status === 'approved' && <button className="button primary" onClick={() => runAction('publish')}><Send size={17}/>Publish to app</button>}
        {platformAdmin && config.status === 'published' && <button className="button secondary" onClick={() => runAction('reopen')}><Undo2 size={17}/>Reopen draft</button>}
        {platformAdmin && publication.historyCount > 0 && <button className="button secondary" onClick={() => runAction('rollback')}><RotateCcw size={17}/>Roll back live version</button>}
        {platformAdmin && publication.live && <button className="button danger-quiet" onClick={() => runAction('unpublish')}>Unpublish</button>}
      </div>
    </header>
    {notice && <div className="studio-notice" role="status"><span>{notice}</span><button onClick={() => setNotice('')} aria-label="Dismiss notification">×</button></div>}
    <div className="workspace">
      <aside className="catalogue">
        {workspaceSwitcher}
        <div className="catalogue-intro"><p className="eyebrow">Trust-owned links</p><h1>Know Your Hospital</h1><p>Patients see translated card titles. The Trust website remains the maintained source of local information.</p></div>
        <div className="hospital-state-key"><strong>Card states</strong><span><i className="available"/>Available: opens Trust URL</span><span><i className="unavailable"/>Unavailable: shows translated notice</span><span><i className="hidden"/>Hidden: removes the card</span></div>
      </aside>
      <main className="review-workspace">
        <div className="role-toolbar"><div><p className="eyebrow">13 fixed patient information areas</p><h1>{scopedTrusts.find((trust) => trust.id === trustId)?.shortName || 'Trust'} hospital links</h1><div className="meta-row"><span>Titles and illustrations are centrally controlled</span><span>Only URLs and availability are editable</span></div></div></div>
        {platformAdmin && config.status === 'submitted' && <div className="review-comment-box"><label>Review comment for the Trust administrator<textarea rows="3" value={reviewComment} onChange={(event) => setReviewComment(event.target.value)} placeholder="Explain any changes needed before approval"/></label></div>}
        {workflow.reviewComment && config.status === 'draft' && <div className="workflow-lock-banner"><Undo2/><div><strong>Changes requested</strong><span>{workflow.reviewComment}</span></div></div>}
        {locked && <div className="workflow-lock-banner"><ShieldCheck/><div><strong>Submitted for review</strong><span>{!platformAdmin && config.status === 'submitted' ? 'This Trust configuration is locked while it awaits review. You can withdraw the submission if you need to make further changes.' : 'This Trust configuration is locked until the platform reviewer approves it or returns it for changes.'}</span></div></div>}
        {publication.live && <div className="publication-live-banner"><ShieldCheck/><div><strong>A reviewed profile is live in the patient app</strong><span>Editing a reopened draft will not change the live version until it is approved and published again.</span></div></div>}
        {platformAdmin && audit.length > 0 && <details className="audit-panel"><summary>Audit history ({audit.length})</summary><ol>{[...audit].reverse().map((event, index) => <li key={`${event.at}-${index}`}><strong>{event.action.replaceAll('_', ' ')}</strong><span>{new Date(event.at).toLocaleString()} · {event.byEmail || event.by}</span>{event.comment && <em>{event.comment}</em>}</li>)}</ol></details>}
        <section className="review-comment-box" aria-label="Trust-specific translation source audit">
          <p className="eyebrow">Trust-authored wording</p>
          <h2>Local language review is separate from the national catalogue</h2>
          <p>We found {trustTextAudit.needsLocalReview} potentially reviewable local text fields, {trustTextAudit.identityContext} names or identities, and {trustTextAudit.deliberatelyEnglishOnly} deliberately English-only fields in this Trust configuration. Some configured fields may repeat national wording; the Trust reviewer must check each source. The CSV identifies this configuration's status and update time; a reopened draft is not the live published release.</p>
          <p>Enabling a patient language does not certify new Trust stage descriptions, links, custom uniform names or accessible labels in that language. Clinical and linguistic approval must be recorded against this Trust’s current publication version.</p>
          <button className="button secondary" type="button" onClick={downloadTrustTextAudit} disabled={sync !== 'connected' || trustTextAudit.sourceCount === 0}><Save size={17}/>Download Trust wording audit CSV</button>
          {sync !== 'connected' && <p>Cloud Trust configuration is not verified. Reconnect before exporting an audit for review.</p>}
        </section>
        <section className="review-comment-box language-access-panel">
          <div>
            <p className="eyebrow">Patient language access</p>
            <h2>Languages available for this Trust</h2>
            <p>Select the languages patients may use during this pilot. English is always retained. Existing Trusts default to all supported languages.</p>
          </div>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={languageDraft === null}
              disabled={locked}
              onChange={(event) => setLanguageDraft(event.target.checked ? null : getEnabledLanguageIds(null))}
            />
            <span>Allow all supported languages</span>
          </label>
          <div className="language-access-grid" aria-label="Patient languages and their English names">
              {LANGUAGES.map((language) => (
                <label className="checkbox-row" key={language.id}>
                  <input
                    type="checkbox"
                    checked={getEnabledLanguageIds(languageDraft).includes(language.id)}
                    disabled={locked || languageDraft === null || language.id === 'en'}
                    onChange={(event) => setLanguageDraft((current) => {
                      const currentIds = getEnabledLanguageIds(current);
                      const nextIds = event.target.checked
                        ? [...new Set([...currentIds, language.id])]
                        : currentIds.filter((id) => id !== language.id);
                      return normalizeEnabledLanguageIds(nextIds);
                    })}
                  />
                  <span><bdi dir={language.direction}>{language.label}</bdi>{englishLanguageName(language) !== language.label ? ` — ${englishLanguageName(language)}` : ''}{language.id === 'en' ? ' (always available)' : ''}</span>
                </label>
              ))}
          </div>
          {languageDraft === null && <p>All supported languages are enabled. Clear “Allow all supported languages” to customise the selection.</p>}
        </section>
        <div className="hospital-info-grid">
          {HOSPITAL_INFO_SECTIONS.map((section) => {
            const Icon = ICONS[section.icon] || Building2;
            const item = draft[section.id];
            return <article className={`hospital-info-editor ${item.state}`} key={section.id}>
              <div className="hospital-info-heading"><span><Icon/></span><div><h2>{section.title}</h2><small>Patient-facing title is translated automatically</small></div></div>
              <label>Status<select disabled={locked} value={item.state} onChange={(event) => update(section.id, { state: event.target.value, url: event.target.value === 'available' ? item.url : '' })}><option value="available">Available — show linked card</option><option value="unavailable">Information not currently available</option><option value="hidden">Hidden — remove card</option></select></label>
              {item.state === 'available' && <label>Trust webpage URL<input disabled={locked} type="url" value={item.url} placeholder="https://www.nhs.uk/…" onChange={(event) => update(section.id, { url: event.target.value })}/>{item.url && !/^https:\/\//i.test(item.url) && <em>Enter a secure https URL</em>}</label>}
              {item.state === 'unavailable' && <p className="hospital-card-note">The patient page will show a translated “information not currently available” message.</p>}
              {item.state === 'hidden' && <p className="hospital-card-note">This card will not appear on the Trust’s patient page.</p>}
            </article>;
          })}
        </div>
      </main>
    </div>
  </div>;
}
